import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Coins, Video, Users, ArrowRight, Send, CalendarCheck, PlayCircle } from "lucide-react";
import { api } from "@/lib/api";
import { TutorCard } from "@/components/TutorBrowser";
import { RequestDialog } from "@/components/RequestDialog";

const HERO = "https://images.unsplash.com/photo-1619852182277-79aa23f82c8e?w=1400&q=80";

const pillars = [
  [ShieldCheck, "Verified tutors", "Every tutor is reviewed and approved by FosterApp admins before they can teach.", "md:col-span-5"],
  [Coins, "100-credit sessions", "Simple, transparent pricing. Credits are reserved on booking and only deducted after you attend.", "md:col-span-7"],
  [Video, "Live video rooms", "Join secure video classrooms straight from your dashboard — no extra setup.", "md:col-span-7"],
  [Users, "Group coaching", "Tutors can bring multiple approved students into one focused live session.", "md:col-span-5"],
];

const steps = [
  [Send, "Request", "Find a tutor that fits and send a coaching request."],
  [CalendarCheck, "Approve & schedule", "Your tutor approves and schedules a live session; 100 credits are reserved."],
  [PlayCircle, "Attend live", "Join the class. After a verified session, exactly 100 credits are deducted."],
];

export default function Home() {
  const [tutors, setTutors] = useState([]);
  const [stats, setStats] = useState(null);
  const [req, setReq] = useState(null);
  useEffect(() => {
    api.get("/tutors", { params: { limit: 3 } }).then((r) => setTutors(r.data.items));
    api.get("/public/stats").then((r) => setStats(r.data));
  }, []);
  return (
    <div>
      <section className="grain relative overflow-hidden bg-[#0B1A33] text-white">
        <div className="grid-texture absolute inset-0" />
        <div className="absolute -right-40 -top-40 h-[520px] w-[520px] rounded-full bg-[#1F6FFF]/25 blur-3xl" />
        <div className="absolute -bottom-40 left-1/3 h-[380px] w-[380px] rounded-full bg-[#3CC34A]/15 blur-3xl" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-4 py-20 sm:px-6 lg:grid-cols-12 lg:px-12 lg:py-28">
          <div className="lg:col-span-7">
            <span className="rise inline-flex rounded-full border border-[#1F6FFF]/30 bg-[#1F6FFF]/15 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-[#00D8F6]">Live coaching platform</span>
            <h1 className="rise d1 mt-6 text-4xl font-extrabold leading-[1.05] sm:text-5xl lg:text-6xl">
              Explore to <span className="text-[#3CC34A]">Excel</span>.<br />Learn live with tutors who get you.
            </h1>
            <p className="rise d2 mt-6 max-w-xl text-base text-slate-300 md:text-lg">FosterApp connects students with verified tutors for live one-to-one and group video sessions — booked with a simple, fair credit wallet.</p>
            <div className="rise d3 mt-9 flex flex-wrap gap-3">
              <Link to="/tutors" data-testid="hero-find-tutors-button" className="inline-flex items-center gap-2 rounded-full bg-[#3CC34A] px-7 py-3 font-bold text-[#0B1A33] transition-colors hover:bg-[#2DA638]">Find a Tutor <ArrowRight className="h-4 w-4" /></Link>
              <Link to="/register/tutor" data-testid="hero-join-tutor-button" className="rounded-full border border-white/25 px-7 py-3 font-semibold text-white transition-colors hover:bg-white/10">Join as Tutor</Link>
            </div>
            <div className="rise d4 mt-12 flex gap-10">
              {[["tutors", "Verified tutors"], ["students", "Active students"], ["sessions", "Sessions completed"]].map(([k, l]) => (
                <div key={k}><p className="font-display text-3xl font-extrabold">{stats?.[k] ?? "–"}</p><p className="text-xs uppercase tracking-wider text-slate-400">{l}</p></div>
              ))}
            </div>
          </div>
          <div className="rise d2 relative lg:col-span-5">
            <div className="overflow-hidden rounded-3xl border border-white/10 shadow-2xl shadow-black/40">
              <img src={HERO} alt="Student in a live video lesson" className="h-[420px] max-h-[80vh] w-full object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-6 rounded-2xl border border-white/10 bg-[#112240]/90 p-4 backdrop-blur-md">
              <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#3CC34A]"><span className="h-2 w-2 animate-pulse rounded-full bg-[#3CC34A]" /> Live now</p>
              <p className="mt-1 text-sm font-semibold">Group calculus · 4 students</p>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-12 lg:py-24">
        <p className="text-xs font-semibold uppercase tracking-wider text-[#1F6FFF]">Why FosterApp</p>
        <h2 className="mt-2 max-w-2xl text-2xl font-bold text-[#0B1A33] sm:text-3xl lg:text-4xl">Built for focused, accountable learning.</h2>
        <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-12">
          {pillars.map(([Icon, t, d, span], i) => (
            <div key={t} className={`lift rounded-2xl border p-8 ${span} ${i === 1 ? "border-[#0B1A33] bg-[#0B1A33] text-white" : "border-slate-200/80 bg-white"}`}>
              <Icon className={`h-8 w-8 ${i === 1 ? "text-[#3CC34A]" : "text-[#1F6FFF]"}`} />
              <h3 className={`mt-5 text-xl font-bold ${i === 1 ? "text-white" : "text-[#0B1A33]"}`}>{t}</h3>
              <p className={`mt-2 max-w-md text-sm ${i === 1 ? "text-slate-300" : "text-slate-500"}`}>{d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-white py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
          <div className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-[#1F6FFF]">Featured tutors</p>
              <h2 className="mt-2 text-2xl font-bold text-[#0B1A33] sm:text-3xl lg:text-4xl">Learn from the best.</h2>
            </div>
            <Link to="/tutors" data-testid="home-all-tutors-link" className="inline-flex items-center gap-2 font-semibold text-[#1F6FFF]">Browse all tutors <ArrowRight className="h-4 w-4" /></Link>
          </div>
          <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3">
            {tutors.map((t) => <TutorCard key={t.id} t={t} profileBase="/tutors" onRequest={setReq} />)}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-12 lg:py-24">
        <p className="text-xs font-semibold uppercase tracking-wider text-[#1F6FFF]">How it works</p>
        <h2 className="mt-2 text-2xl font-bold text-[#0B1A33] sm:text-3xl lg:text-4xl">Three steps to your first live class.</h2>
        <div className="mt-12 grid gap-8 md:grid-cols-3">
          {steps.map(([Icon, t, d], i) => (
            <div key={t} className="relative border-l-2 border-[#3CC34A] pl-6">
              <span className="font-display text-5xl font-extrabold text-slate-200">0{i + 1}</span>
              <Icon className="mt-2 h-6 w-6 text-[#1F6FFF]" />
              <h3 className="mt-3 text-lg font-bold text-[#0B1A33]">{t}</h3>
              <p className="mt-1 text-sm text-slate-500">{d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="px-4 pb-20 sm:px-6 lg:px-12">
        <div className="grain relative mx-auto max-w-7xl overflow-hidden rounded-3xl bg-[#0B1A33] p-10 text-white lg:p-16">
          <div className="grid-texture absolute inset-0" />
          <div className="relative flex flex-col justify-between gap-8 md:flex-row md:items-center">
            <div>
              <h2 className="text-2xl font-bold sm:text-3xl">Ready to excel?</h2>
              <p className="mt-2 text-slate-300">Create your free student account in under a minute.</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link to="/register/student" data-testid="cta-register-student" className="rounded-full bg-[#3CC34A] px-7 py-3 font-bold text-[#0B1A33] hover:bg-[#2DA638]">Register as Student</Link>
              <Link to="/register/tutor" data-testid="cta-register-tutor" className="rounded-full border border-white/25 px-7 py-3 font-semibold hover:bg-white/10">Apply as Tutor</Link>
            </div>
          </div>
        </div>
      </section>
      <RequestDialog tutor={req} onClose={() => setReq(null)} />
    </div>
  );
}
